import type { DecodeRequest, DecodeResponse } from "./protocol";
import type { DecodedTrackingJob, TrackingInterval, TrackingOptions } from "../contracts";
import type { RecoveryFrameRequest, RecoveryImage } from "../recovery/contracts";

type DecodeSuccess = Exclude<DecodeResponse, { type: "error" }>;

/** One shared video decoder. Cropped buffers move through this client without copying pixels. */
export class DecodeClient {
  private worker: Worker;
  private sequence = 0;
  private disposed = false;
  private terminalError: Error | null = null;
  private pending: {
    id: number;
    resolve: (response: DecodeSuccess) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  } | null = null;

  constructor(url: string, options: TrackingOptions) {
    const sourceUrl = new URL(url, document.baseURI).href;
    this.worker = new Worker(new URL("./decode-worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = ({ data }: MessageEvent<DecodeResponse>) => {
      if (data.type === "error" && data.id === -1) {
        this.dispose(new Error(data.message));
        return;
      }
      if (!this.pending || this.pending.id !== data.id) return;
      const request = this.pending;
      this.pending = null;
      clearTimeout(request.timeout);
      if (data.type === "error") request.reject(new Error(data.message));
      else request.resolve(data);
    };
    this.worker.onerror = (event) => this.dispose(new Error(event.message || "Video decoder worker failed"));
    this.worker.onmessageerror = () => this.dispose(new Error("Invalid video decoder worker message"));
    this.send({ type: "init", url: sourceUrl, options });
  }

  get isDisposed() {
    return this.disposed;
  }

  decode(intervals: TrackingInterval[]): Promise<DecodedTrackingJob[]> {
    return this.request({ type: "decode", id: this.sequence + 1, intervals }, (response) => {
      if (response.type !== "decoded") throw new Error("Unexpected video decoding response");
      return response.jobs;
    });
  }

  extractRecoveryImages(requests: readonly RecoveryFrameRequest[]): Promise<RecoveryImage[]> {
    return this.request({ type: "extract-recovery-images", id: this.sequence + 1, requests }, (response) => {
      if (response.type !== "recovery-images") throw new Error("Unexpected recovery image response");
      return response.images;
    });
  }

  private request<Result>(
    message: Exclude<DecodeRequest, { type: "init" }>,
    select: (response: DecodeSuccess) => Result,
  ): Promise<Result> {
    if (this.disposed) return Promise.reject(this.terminalError ?? new Error("Video decoder is disposed"));
    if (this.pending) return Promise.reject(new Error("Video decoder already has an active batch"));
    this.sequence = message.id;
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => this.dispose(new Error("Video decoding exceeded the 120-second limit")),
        120_000,
      );
      this.pending = {
        id: message.id,
        resolve: (response) => {
          try {
            resolve(select(response));
          } catch (error) {
            reject(error);
          }
        },
        reject,
        timeout,
      };
      this.send(message);
    });
  }

  private send(message: DecodeRequest) {
    try {
      this.worker.postMessage(message);
    } catch (error) {
      this.dispose(error instanceof Error ? error : new Error(String(error)));
    }
  }

  dispose(error = new Error("Frame decoding cancelled")) {
    if (this.disposed) return;
    this.disposed = true;
    this.terminalError = error;
    this.worker.terminate();
    if (this.pending) {
      clearTimeout(this.pending.timeout);
      this.pending.reject(error);
      this.pending = null;
    }
  }
}
